import React from 'react';
import { NavBadge } from './NavBadge';
import { Link, type Location } from 'react-router-dom';
import { SrIcon } from '@/components/ui/SrIcon';
import { cn } from '@/lib/utils';
import { isNavChildActive, type NavItem } from './nav-items';
import { useNavGroupState } from './use-nav-group-state';
import type { SidebarShortcut } from '@/hooks/use-sidebar-shortcuts';

interface AppMobileNavProps {
  filteredNav: NavItem[];
  location: Location;
  innstillingerOpen: boolean;
  setInnstillingerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleNavClick: () => void;
  shortcuts: SidebarShortcut[];
  isShortcutActive: (shortcutPath: string) => boolean;
}

export default function AppMobileNav({
  filteredNav,
  location,
  innstillingerOpen,
  setInnstillingerOpen,
  handleNavClick,
  shortcuts,
  isShortcutActive,
}: AppMobileNavProps) {
  const groups = useNavGroupState(location, innstillingerOpen, setInnstillingerOpen);
  return (
    <nav aria-label="Hovedmeny" className="flex flex-col py-4">
      {filteredNav.map((item) => {
        if (item.children) {
          const groupActive = location.pathname.startsWith(item.path);
          const isOpen = groups.isOpen(item);
          return (
            <div key={item.path}>
              <button
                type="button"
                onClick={() => groups.toggle(item)}
                aria-expanded={isOpen}
                className={cn(
                  'flex items-center gap-3 px-6 py-3 text-[15px] transition-all border-l-2 border-transparent w-full text-left',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/80',
                  groupActive
                    ? 'bg-[rgb(3,30,255,0.22)] !border-[var(--sr-reveblaa)] text-white font-medium'
                    : 'text-white/70 hover:text-white hover:bg-white/[0.04]',
                )}
              >
                <SrIcon name={item.icon} size={20} />
                <span className="flex-1 min-h-[44px] flex items-center">{item.label}</span>
                <SrIcon
                  name={isOpen ? 'chevron-down' : 'chevron-right'}
                  size={14}
                  className="opacity-60"
                />
              </button>
              {isOpen && (
                <div className="bg-black/10">
                  {item.children.map((child) => {
                    const childActive = isNavChildActive(child, item.children!, location.pathname);
                    return (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={handleNavClick}
                        aria-current={childActive ? 'page' : undefined}
                        className={cn(
                          'flex items-center gap-3 pl-14 pr-6 py-2 text-[14px] transition-all border-l-2 border-transparent',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/80',
                          childActive
                            ? 'bg-[rgb(3,30,255,0.16)] !border-[var(--sr-reveblaa)] text-white font-medium'
                            : 'text-white/60 hover:text-white hover:bg-white/[0.03]',
                        )}
                      >
                        <SrIcon name={child.icon} size={16} />
                        <span className="min-h-[40px] flex items-center">{child.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        const active =
          location.pathname === item.path ||
          (item.path !== '/' && location.pathname.startsWith(item.path));
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={handleNavClick}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex items-center gap-3 px-6 py-3 text-[15px] transition-all border-l-2 border-transparent',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/80',
              active
                ? 'bg-[rgb(3,30,255,0.22)] !border-[var(--sr-reveblaa)] text-white font-medium'
                : 'text-white/70 hover:text-white hover:bg-white/[0.04]',
            )}
          >
            <SrIcon name={item.icon} size={20} />
            <span className="min-h-[44px] flex items-center">{item.label}</span>
            <NavBadge count={item.badge} />
          </Link>
        );
      })}

      {shortcuts.length > 0 && (
        <>
          <div className="my-3 mx-6 border-t border-white/10" />
          <div className="px-6 pb-1 text-[11px] font-medium uppercase tracking-[0.09em] text-white/40">
            Snarveier
          </div>
          {shortcuts.map((s) => {
            const active = isShortcutActive(s.path);
            return (
              <Link
                key={s.key}
                to={s.path}
                onClick={handleNavClick}
                className={cn(
                  'flex items-center gap-3 px-6 py-2.5 text-[14px] transition-all border-l-2 border-transparent',
                  active
                    ? 'bg-[rgb(3,30,255,0.22)] !border-[var(--sr-reveblaa)] text-white font-medium'
                    : 'text-white/60 hover:text-white hover:bg-white/[0.04]',
                )}
              >
                <SrIcon name={s.icon} size={18} />
                <span className="min-h-[44px] flex items-center">{s.label}</span>
              </Link>
            );
          })}
        </>
      )}
    </nav>
  );
}
